import { useCallback, useEffect, useState } from 'react';
import {
  ONBOARDING_RECOMMEND_PLAN,
  ONBOARDING_RECOMMEND_PLAN_FORCE,
} from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useStorage } from 'commons/hooks';
import {
  useCanEnterAdmin,
  useGetStoreInfo,
  useGoToPlans,
  useIsLogged,
} from 'domains/Auth/hooks';
import {
  useLazyGetRecommendPlanStatusQuery,
  useUpdateRecommendPlanStatusMutation,
} from '../pages/RecommendPlanPage/recommendPlanApi';

export const RECOMMEND_PLAN_COMPLETED =
  'pre-onboarding-recommend-plan-completed';

export function useRecommendPlanStatus() {
  const { id } = useGetStoreInfo();
  const canEnterAdmin = useCanEnterAdmin();
  const { isLogin } = useIsLogged();
  const [hasForcedTag, hasEnabledTag] = useHasTags([
    ONBOARDING_RECOMMEND_PLAN_FORCE,
    ONBOARDING_RECOMMEND_PLAN,
  ]);
  const hasEnabledTagOrForced = hasEnabledTag || hasForcedTag;
  const { goToPlans } = useGoToPlans();

  const [updateRecommendPlanStatus] = useUpdateRecommendPlanStatusMutation();
  const [
    getRecommendPlanStatus,
    {
      data: recommendPlanStatus,
      isLoading: isLoadingRecommendPlanStatus,
      isError: isErrorRecommendPlanStatus,
      isUninitialized: isUninitializedRecommendPlanStatus,
    },
  ] = useLazyGetRecommendPlanStatusQuery();

  const localStorageItem = `${RECOMMEND_PLAN_COMPLETED}-${id}`;
  const [
    recommendPlanCompleted,
    setRecommendPlanCompleted,
    recommendPlanStorageLoaded,
  ] = useStorage(localStorageItem, false);

  const isRecommendPlanCompleted =
    recommendPlanCompleted || recommendPlanStatus?.planSelected;

  const [showRecommendPlan, setShowRecommendPlan] = useState(false);

  const onCompleteRecommendPlan = useCallback(
    (redirectToPlans = false) => {
      updateRecommendPlanStatus({ planSelected: true })
        .unwrap()
        .then(() => {
          setRecommendPlanCompleted(true);
          if (redirectToPlans) {
            goToPlans();
          }
        });
    },
    [updateRecommendPlanStatus, setRecommendPlanCompleted, goToPlans],
  );

  const skipRecommendPlan = useCallback(() => {
    setShowRecommendPlan(false);
  }, [setShowRecommendPlan]);

  useEffect(() => {
    if (!isLoadingRecommendPlanStatus && isRecommendPlanCompleted) {
      setRecommendPlanCompleted(true);
    }
  }, [
    isRecommendPlanCompleted,
    isLoadingRecommendPlanStatus,
    setRecommendPlanCompleted,
  ]);

  useEffect(() => {
    if (
      hasEnabledTagOrForced &&
      isLogin &&
      recommendPlanStorageLoaded &&
      !isRecommendPlanCompleted &&
      !isErrorRecommendPlanStatus
    ) {
      getRecommendPlanStatus();
    }
  }, [
    hasEnabledTagOrForced,
    isLogin,
    recommendPlanStorageLoaded,
    isRecommendPlanCompleted,
    isErrorRecommendPlanStatus,
    getRecommendPlanStatus,
  ]);

  useEffect(() => {
    if (
      (hasEnabledTagOrForced &&
        recommendPlanStorageLoaded &&
        isRecommendPlanCompleted) ||
      isErrorRecommendPlanStatus ||
      !canEnterAdmin
    ) {
      setShowRecommendPlan(false);
    } else {
      setShowRecommendPlan(
        hasEnabledTagOrForced &&
          (isUninitializedRecommendPlanStatus ||
            isLoadingRecommendPlanStatus ||
            !isRecommendPlanCompleted),
      );
    }
  }, [
    hasEnabledTagOrForced,
    recommendPlanStorageLoaded,
    isRecommendPlanCompleted,
    isErrorRecommendPlanStatus,
    canEnterAdmin,
    isUninitializedRecommendPlanStatus,
    isLoadingRecommendPlanStatus,
    setShowRecommendPlan,
  ]);

  return {
    showRecommendPlan,
    isLoadingRecommendPlanStatus:
      isLoadingRecommendPlanStatus || !recommendPlanStorageLoaded,
    skipRecommendPlan,
    onCompleteRecommendPlan,
  };
}
